/* eslint-disable no-param-reassign */
/* eslint-disable no-unneeded-ternary */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-lonely-if */

import React, { useState, useEffect, useRef, type FC, type CSSProperties } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
// eslint-disable-next-line import/no-cycle
import { FieldType, type ControlProps, type Option, type Target } from '../../index';
import { ContainerStyled } from './styled';
import FieldLabel from '../common/FieldLabel';
import Button from './Button';
import List from './List';
import { sumClasses } from '../../../../utils/misc';
import { LI_HEIGHT_NUMBER } from '../../../../styles/constants';

export interface ListBoxProps extends ControlProps {
  label?: any;
  options: Option[];
  value?: any;
  default?: any;
  updateOnClickOut?: boolean;
  minEl?: number;
  expanded?: boolean;
  limit?: number;
  replaceText?: string;
  columns?: number;
  iconOnTheRight?: boolean;
  name: string;
  onUpdate: (e: Target) => Promise<void>;
}

const ListBox: FC<ListBoxProps> = (props) => {
  const {
    label,
    value,
    type,
    expanded,
    name,
    className,
    style,
    disabled,
    default: defaultProps,
    onUpdate,
    updateOnClickOut,
    limit,
    replaceText,
    columns,
    iconOnTheRight,
  } = props;
  const [options, setOptions] = useState(props.options);
  const innerRef = useRef<HTMLDivElement>(null);
  const listref = useRef<HTMLUListElement>(null);
  const isListBox = type === FieldType.LISTBOX;
  const selectedItem = options.filter((o) => o.value === value);
  const optionsArray =
    value && value.length > 0
      ? isListBox
        ? selectedItem[0]
        : options.filter((o) => value.indexOf(o.value) !== -1)
      : [];
  const defaultOption = options.filter((o) => o.value === props.default);
  const hasOptions = options.length > 0;
  const [currentSelection, setCurrentSelection] = useState(
    isListBox
      ? hasOptions
        ? value !== props.default
          ? selectedItem[0]
          : defaultOption[0]
        : {}
      : hasOptions
        ? value && value.length > 0
          ? optionsArray
          : defaultOption
        : []
  );
  const [currentIndex, setCurrentIndex] = useState(
    isListBox
      ? hasOptions
        ? value !== props.default
          ? options.findIndex((o) => o.value === value)
          : 0
        : 0
      : hasOptions
        ? value && value.length > 0
          ? options.findIndex((o) => o.value === value[0])
          : 0
        : 0
  );
  const [listClassName, setListClassName] = useState(expanded ? '' : 'hidden');

  const listboxHeightTwoColumns = {
    height: ((options.length % 2 ? options.length + 1 : options.length) * LI_HEIGHT_NUMBER) / 2,
  };
  const [listboxStyle, setListboxStyle] = useState<CSSProperties>(columns === 2 ? listboxHeightTwoColumns : {});

  const [buttonStyle, setButtonStyle] = useState<CSSProperties>({});
  const [addButtonProps, setAddButtonProps] = useState(expanded ? { 'aria-expanded': 'true' } : {});

  useEffect(() => {
    if (value) {
      if (isListBox) {
        handleItemClick(options.filter((x: any) => x.value === value)[0], true);
      } else {
        if (value && value.length > 0) {
          value.forEach((x: any) => {
            if ((currentSelection as Option[]).filter((y) => y.value === x).length === 0) {
              handleItemClick(options.filter((y: any) => y.value === x)[0], true);
            }
          });
        } else {
          setCurrentIndex(0);
          setCurrentSelection(isListBox ? defaultOption[0] : defaultOption);
        }
      }
    } else if (value === props.default) {
      setCurrentIndex(0);
      setCurrentSelection(isListBox ? defaultOption[0] : defaultOption);
    }
  }, [value]);

  const adjustHeight = () => {
    if (innerRef && innerRef.current && listref && listref.current && listref.current.firstChild) {
      if (!innerRef.current.offsetParent) return;

      const { top, height } = innerRef.current.offsetParent.getBoundingClientRect();
      const labelHeight = innerRef.current.previousSibling
        ? (innerRef.current.previousSibling as HTMLElement).offsetHeight
        : 0;
      const firstChildHeight = (listref.current.childNodes[1] as HTMLElement).offsetHeight;
      const maxHeight = (props.minEl ? props.minEl : props.options.length) * firstChildHeight;
      const innerHeight = canUseDOM ? window.innerHeight : 0;
      const isOpen = (innerRef.current as HTMLDivElement).getAttribute('aria-expanded');
      const buttonStyleCommon = {
        backgroundColor: '#ffffff',
        fontWeight: 'bold',
        color: '#3C3C3C',
      };

      if (maxHeight > innerHeight - (top + height)) {
        setListboxStyle({
          maxHeight,
          top: `-${maxHeight - labelHeight}px`,
          boxShadow: '0px -1px 4px 0px #dddddf',
          borderRadius: '9px 9px 0 0',
        });
        if (replaceText) {
          setButtonStyle(
            isOpen
              ? {
                  borderRadius: '0 0 9px 9px',
                  ...buttonStyleCommon,
                  borderTop: '1px solid #EAEAEB',
                }
              : {}
          );
        }
      } else {
        setListboxStyle({
          maxHeight,
          top: `${innerRef.current.offsetHeight + labelHeight}px`,
          borderRadius: '0 0 9px 9px',
        });
        if (replaceText) {
          setButtonStyle(
            isOpen
              ? {
                  borderRadius: '9px 9px 0 0',
                  borderBottom: '1px solid #EAEAEB',
                  ...buttonStyleCommon,
                }
              : {}
          );
        }
      }
    }
  };

  const handleClickOut = (val: any) => {
    if (!expanded && listClassName === '') {
      if (listClassName === '') val = 'hidden';
      setListClassName(val);
      setAddButtonProps(val === '' ? { 'aria-expanded': 'true' } : {});
      if (type === FieldType.MULTILISTBOX && updateOnClickOut && listClassName === '') {
        onUpdate({
          target: {
            name,
            value: (currentSelection as Option[]).filter((o) => o.value !== props.default).map((o: any) => o.value),
          },
        });
      }
    }
  };

  const scrollOnFocus = (item: any, newIndex: any) => {
    const listbox = listref.current;

    if (listbox) {
      const element = (listbox as HTMLUListElement).querySelector(`#exp_elem_${name}_${item.value}`);
      if (element) {
        if (listbox.scrollHeight > listbox.clientHeight) {
          const scrollBottom = listbox.clientHeight + listbox.scrollTop;
          const elementBottom = (element as HTMLLIElement).offsetTop + (element as HTMLLIElement).offsetHeight;
          if (elementBottom > scrollBottom) {
            listbox.scrollTop = elementBottom - listbox.clientHeight;
          } else if ((element as HTMLLIElement).offsetTop < listbox.scrollTop) {
            listbox.scrollTop = (element as HTMLLIElement).offsetTop;
          }
        }
      }
    }

    setSelected(newIndex);
  };

  const setSelected = (index: any) => {
    for (let i = 0; i < options.length; i++) {
      const listbox = listref.current;
      const element = (listbox as HTMLUListElement).querySelector(`#exp_elem_${name}_${options[i].value}`);

      if (element) {
        if (i === index) {
          element.setAttribute('aria-selected', 'true');
        } else {
          element.removeAttribute('aria-selected');
        }
      }
    }
  };

  const handleItemClick = (item: any, isEffect?: boolean) => {
    if (!disabled && item) {
      const currentIndexNew = options.findIndex((o: any) => o.value === item.value);

      if (type === FieldType.LISTBOX) {
        setCurrentSelection(item);
        setListClassName('hidden');
        setAddButtonProps({});
        setCurrentIndex(currentIndexNew);
        if (!isEffect) onUpdate({ target: { name, value: item.value } });
      } else {
        let newSelection = currentSelection as Option[];
        if ((currentSelection as Option[]).filter((o) => o.value === item.value).length > 0) {
          newSelection = (currentSelection as Option[]).filter((o) => o.value !== item.value);
        } else {
          newSelection.push(item);
        }
        if (newSelection.length === 0) {
          newSelection = defaultOption;
          setSelected(0);
        }

        setCurrentSelection(newSelection);
        if (!updateOnClickOut) {
          setAddButtonProps({});
        }
        setCurrentIndex(currentIndexNew);

        if (!updateOnClickOut && !isEffect)
          onUpdate({
            target: { name, value: newSelection.filter((o) => o.value !== props.default).map((o) => o.value) },
          });
      }
    }
  };

  const labelId = `lb_label_${name}`;
  const buttonId = `lb_button_${name}`;
  const listId = `lb_list_${name}`;
  const defaultValue = props.default;

  const ref = useOnclickOutside(() => {
    if (!props.expanded) handleClickOut('hidden');
  });

  const fieldLabel = label && JSON.parse(JSON.stringify(label));
  if (fieldLabel) fieldLabel.className = sumClasses(['exp_elem', label.className]);

  const isOpen = (innerRef?.current as HTMLDivElement)?.getAttribute('aria-expanded');

  useEffect(() => {
    if (props.options !== options) {
      setOptions(props.options);
    }
  }, [props.options]);

  return (
    <ContainerStyled
      {...{
        ref,
        className: sumClasses([
          'container-field',
          type ? type.toString() : '',
          className ? className : '',
          expanded ? 'expanded' : '',
          isOpen ? 'open' : 'closed',
        ]),
        style,
      }}
      disabled={disabled}
    >
      {label ? <FieldLabel {...{ label: fieldLabel, name: buttonId, labelId }} /> : null}
      {expanded ? null : (
        <Button
          buttonId={buttonId}
          labelId={labelId}
          innerRef={innerRef}
          defaultProps={defaultProps}
          disabled={disabled}
          name={name}
          options={options}
          currentSelection={currentSelection}
          currentIndex={currentIndex}
          value={value}
          listClassName={listClassName}
          addButtonProps={addButtonProps}
          defaultValue={defaultValue}
          updateOnClickOut={updateOnClickOut}
          type={type}
          replaceText={replaceText}
          buttonStyle={buttonStyle}
          expanded={expanded}
          adjustHeight={() => adjustHeight()}
          setSelected={(e: any) => setSelected(e)}
          setCurrentSelection={(e: any) => setCurrentSelection(e)}
          setCurrentIndex={(e: any) => setCurrentIndex(e)}
          setListClassName={(e: any) => setListClassName(e)}
          scrollOnFocus={(a: any, b: any) => scrollOnFocus(a, b)}
          setButtonProps={(e: any) => setAddButtonProps(e)}
          onUpdate={onUpdate}
          handleClickOut={handleClickOut}
        />
      )}
      <List
        listId={listId}
        labelId={labelId}
        listClassName={listClassName}
        options={options}
        name={name}
        currentIndex={currentIndex}
        listboxStyle={listboxStyle}
        currentSelection={currentSelection}
        listref={listref}
        defaultValue={defaultValue}
        type={type}
        expanded={expanded}
        limit={limit}
        columns={columns}
        handleItemClick={(e: any) => handleItemClick(e)}
        setSelected={(e: any) => setSelected(e)}
        setCurrentSelection={(e: any) => setCurrentSelection(e)}
        onUpdate={onUpdate}
        setCurrentIndex={(e: any) => setCurrentIndex(e)}
        iconOnTheRight={iconOnTheRight}
      />
    </ContainerStyled>
  );
};

export default ListBox;
